// ── PositionSidebar — Floating position tracker popup ──────────────
// Renders as a fixed-position panel anchored to the right edge so it can
// overlay the table + detail panel without fighting either for layout
// space. The crosshair button in the toolbar opens/closes it; click
// outside or Escape dismisses.
// Reads from accountStore + marketStore via shared usePositionPnl hook.

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Crosshair, X, ExternalLink, Receipt, FoldVertical, UnfoldVertical } from 'lucide-react';
import { cn } from '@/lib/infra/utils';

const SIDEBAR_WIDTH_KEY = 'positionSidebar.width';
const SIDEBAR_WIDTH_DEFAULT = 384; // w-96
const SIDEBAR_WIDTH_MIN = 256;
const SIDEBAR_WIDTH_MAX = 800;
import { useAccountStore } from '../../stores/accountStore';
import { isKalshiCancelPending } from '../../lib/trading/kalshiCancelLifecycle';
import { useDisplaySettings } from '../../stores/displaySettings';
import { describeWithheldTotal, withheldHeadlineText } from '../../lib/accounting/unrealizedCoverage';
import { usePositionPnl, MTM_PNL_TOOLTIP } from '../../hooks/ui/usePositionPnl';
import { usePolyBalance } from '../../hooks/usePolyBalance';
import { usePolyUnrealizedPnl } from '../../hooks/usePolyUnrealizedPnl';
import { usePolymarketRelayOrders } from '../../hooks/usePolymarketRelayOrders';
import { cancelPolymarketUsOrder } from '@/lib/polymarketUs/relay';
import type { Venue } from '@/types';
import { describePolyMarketCached } from '../../lib/polymarket/describeOrder';
import { explainPolyError } from '../../lib/polymarket/errors';
import { polyOpenOrderRemaining } from '../../lib/polymarket/relay';
import { describePolyCancelRow } from '../../lib/trading/polyCancelPresentation';
import { usePolyCancelStates } from '../../hooks/usePolyCancelStates';
import { ActivityTicker } from '../../components/ActivityTicker';
import { getAudioContext } from '../../lib/audio/audioContext';
import { describeMarket } from '../../lib/kalshiTicker';
import { ConfirmModal } from '../../components/atoms/Modal';
import { VenueChip } from '../../components/atoms/VenueChip';
import { EditOrderPriceModal, OrderActionButtons } from '../../components/molecules';
import { fmtQty } from '../../components/cards/cardFormat';
import { kalshiReportedBalanceTitle } from '../../lib/kalshiBalance';

// Local edit-modal target — same shape as PositionTracker's EditOrderData
// but Kalshi-only here (poly rows are display + cancel; poly price edits
// live on the Positions page).
interface SidebarEditOrder {
  orderId: string;
  marketLabel: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  remainingCount: number;
  currentPriceCents: number;
  ticker: string;
}

function formatDollars(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function formatCents(cents: number): string {
  return `${cents}\u00A2`;
}

type PositionsSyncView = { synced: boolean; consecutiveFailures: number; lastError: string | null };

/** Headline tooltip (#881 review F4): says WHY a value is withheld. An
 *  unreadable snapshot is named as such; a failed refresh after a good
 *  snapshot withholds the account total on that known failed read (counted,
 *  never aged, so no freshness deadline is implied) while rows keep the last
 *  readable holdings. */
function kalshiPnlTitle(
  totalPnl: number | null,
  coverage: { pricedPositions: number; totalPositions: number; pricedValueCents: number },
  positionsSync: PositionsSyncView,
  unpricedSummary: string | null,
): string {
  if (!positionsSync.synced) {
    return positionsSync.lastError
      ? `Kalshi positions snapshot unreadable (${positionsSync.lastError}); P&L withheld`
      : 'Kalshi positions snapshot not received yet; P&L withheld';
  }
  const n = positionsSync.consecutiveFailures;
  if (n > 0) {
    return `Kalshi account P&L withheld: the last ${n} position refresh${n === 1 ? '' : 'es'} failed`
      + `${positionsSync.lastError ? ` (${positionsSync.lastError})` : ''}; rows show the last readable snapshot`;
  }
  return totalPnl === null
    ? `Kalshi unrealized P&L unavailable: ${describeWithheldTotal(coverage, unpricedSummary, formatDollars)}`
    : 'Kalshi account-wide unrealized P&L';
}

/** Short visible warning beside the headline. Carries no money and no
 *  position counts, so it stays visible with P&L hidden; the reason rides
 *  the tooltip only when P&L is shown. */
function positionsRefreshWarning(positionsSync: PositionsSyncView): string | null {
  if (!positionsSync.synced) return positionsSync.lastError ? 'Positions unreadable' : null;
  return positionsSync.consecutiveFailures > 0
    ? `Positions refresh failed ×${positionsSync.consecutiveFailures}`
    : null;
}

export function PositionsRefreshWarning({ positionsSync, showPnl, title }: { positionsSync: PositionsSyncView; showPnl: boolean; title: string }) {
  const text = positionsRefreshWarning(positionsSync);
  if (!text) return null;
  return (
    <span
      className="block text-[10px] leading-4 font-medium text-amber-500"
      data-testid="positions-refresh-warning"
      title={showPnl ? title : 'Kalshi positions refresh problem; P&L hidden'}
    >
      {text}
    </span>
  );
}

export function PositionSidebar({
  onClose,
  onOpenDetail,
  variant = 'overlay',
  section = 'all',
  collapsed = false,
  onToggleCollapse,
  expanded = false,
  onToggleExpand,
}: {
  onClose: () => void;
  /** When provided, the popup title becomes a button that takes the user to
   *  the full Position Tracker tab (and closes the popup). */
  onOpenDetail?: () => void;
  /** Legacy prong collapse (header-only stat line). Superseded on the
   *  positions-page rail by the accordion expand below; kept for API
   *  compatibility with any overlay caller. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Positions-page rail accordions (Brendan, Jul 8): the card is always
   *  content-sized — never taller than its rows need. Resting
   *  (expanded=false) additionally caps the body at RESTING_CAP and
   *  scrolls it internally; expanded uncaps it (no internal scroll — the
   *  rail's stack scroller takes over). When onToggleExpand is provided
   *  the header icon becomes the expand/rest control. */
  expanded?: boolean;
  onToggleExpand?: () => void;
  /** 'overlay' (default): floating panel fixed to the top-right, dismissed on
   *  click-outside / Escape — the Value Dashboard usage. 'docked': renders
   *  inline filling its parent (a sticky aside on the Positions page) as a
   *  content-pushing sidebar — no fixed positioning, resize handle, or
   *  click-outside dismiss. */
  variant?: 'overlay' | 'docked';
  /** Which slice to render. 'all' (default) = the full Value Dashboard
   *  overlay (tracker + recent fills). 'tracker' = P&L + positions + open
   *  orders only. 'fills' = recent fills only. The positions-page rail splits
   *  into two single-section prongs so each docks / pops out / hides on its
   *  own. */
  section?: 'all' | 'tracker' | 'fills';
}) {
  const status = useAccountStore((s) => s.status);
  const openOrders = useAccountStore((s) => s.openOrders);
  const balance = useAccountStore((s) => s.balance);
  const keyMode = useAccountStore((s) => s.keyMode);
  // Poly twins for the money lines (accountStore/usePositionPnl are
  // Kalshi-only). Gated off the fills prong — it renders neither — and
  // balance additionally off the docked variant (the rail's pinned header
  // band owns the balance readout there; polling twice would be waste).
  // Deliberately NOT gated on the Kalshi connection status: poly data
  // needs no Kalshi, and a Kalshi outage (Thu 3–5am ET maintenance) must
  // not blank the poly numbers (Brendan, Jul-9).
  const polyBalance = usePolyBalance(section !== 'fills' && variant === 'overlay');
  const polyPnl = usePolyUnrealizedPnl(section !== 'fills');
  const polyPnlValue = polyPnl?.valueCents ?? null;
  const polyPnlCoverageTitle =
    polyPnl == null
      ? 'Polymarket unrealized P&L is loading'
      : polyPnl.complete
        ? `${polyPnl.pricedPositions}/${polyPnl.totalPositions} Polymarket positions fully priced`
        : `Polymarket unrealized P&L withheld: ${polyPnl.pricedPositions}/${polyPnl.totalPositions} positions have a live mark, open-fee allocation, and close-fee schedule`;
  // Polymarket open orders (Gitea #49 — poly-first traders read the gap as
  // lost orders). The list fetch is module-shared inside the hook, so this
  // mount rides the SAME 5s poll as PositionTracker's when both are up
  // (docked rail) instead of doubling the relay traffic. Fills prong
  // renders no orders — keep it inert there.
  const polyOrdersState = usePolymarketRelayOrders({ enabled: section !== 'fills' });
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const replaceOrder = useAccountStore((s) => s.replaceOrder);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  // Mirror the positions-page masking (default hidden) so the trade-surface
  // sidebar can't leak the red number the stakeholder asked to hide.
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const { positionsWithPnl, totalPnl, coverage, positionsSync, unpricedSummary } = usePositionPnl();
  const kalshiPnlCoverageTitle = kalshiPnlTitle(totalPnl, coverage, positionsSync, unpricedSummary);
  const kalshiPnlText = !positionsSync.synced || positionsSync.consecutiveFailures > 0
    ? '—'
    : totalPnl === null
      ? withheldHeadlineText(coverage, formatDollars)
      : formatDollars(totalPnl);
  const [editOrder, setEditOrder] = useState<SidebarEditOrder | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{
    venue: Venue;
    marketSlug?: string;
    orderId: string;
    label: string;
  } | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // #241 — shared cancel fence. Timeout notices are derived, never copied into
  // local state, so a retry or an authoritative removal clears them on its own.
  const kalshiCancelStates = useAccountStore((s) => s.kalshiCancelStates);
  // #748 — the Polymarket half of the same idea. This row used to read no
  // shared state at all: a cancel started from Position Tracker left this ✕
  // looking untouched, and the refusal surfaced in the error box at the bottom
  // of the sidebar rather than on the row that was clicked.
  const polyCancelStates = usePolyCancelStates();
  const timedOutCancels = Object.entries(kalshiCancelStates).filter(
    ([, state]) => state?.phase === 'timed-out',
  );
  // Mark-to-market dollar value of all open positions. Falls back to
  // averagePrice when hydra hasn't streamed a current mark yet so the figure
  // stays populated. Mirrors the Position Tracker stat card.
  const positionsValueDollars = positionsWithPnl.reduce((sum, p) => {
    const priceCents = p.currentPrice ?? p.averagePrice;
    return sum + (priceCents * Math.abs(p.position)) / 100;
  }, 0);

  // AudioContext state indicator — poll every 2s
  const [audioState, setAudioState] = useState<string>('suspended');
  useEffect(() => {
    const check = () => setAudioState(getAudioContext()?.state ?? 'unavailable');
    check();
    const id = setInterval(check, 2000);
    return () => clearInterval(id);
  }, []);

  const executeCancel = async (venue: Venue, orderId: string, marketSlug?: string) => {
    setCancelError(null);
    if (venue === 'polymarket_us') {
      // F3: the venue's cancel needs the market slug beside the id; the relay
      // recovers it from the audit ledger when the caller cannot supply it.
      try {
        await cancelPolymarketUsOrder(orderId, marketSlug);
      } catch (err) {
        setCancelError(err instanceof Error && err.message ? err.message : 'Cancel failed. Check open orders and try again.');
      }
      return;
    }
    if (venue === 'polymarket') {
      // Hook does the optimistic removal + reconcile refresh; it reports
      // failures as a value rather than throwing.
      const out = await polyOrdersState.cancel(orderId);
      if (!out.ok) setCancelError(explainPolyError(out.error));
      return;
    }
    try {
      await cancelOrder(orderId);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Cancel failed. Check open orders and try again.';
      setCancelError(message);
    }
  };

  const requestCancel = (venue: Venue, orderId: string, label: string, marketSlug?: string) => {
    setCancelError(null);
    if (confirmOrderCancel) {
      setPendingCancel({ venue, orderId, label, marketSlug });
      return;
    }
    void executeCancel(venue, orderId, marketSlug);
  };

  // Resizable width — drag left border, persist to localStorage
  const [width, setWidth] = useState<number>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(SIDEBAR_WIDTH_KEY) : null;
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) && n >= SIDEBAR_WIDTH_MIN && n <= SIDEBAR_WIDTH_MAX ? n : SIDEBAR_WIDTH_DEFAULT;
  });
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleResizePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;

    const onMove = (ev: PointerEvent) => {
      // Anchored on the right, so dragging left (decreasing clientX) makes it wider
      const next = Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, startWidth + (startX - ev.clientX)));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(widthRef.current));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Dismiss on Escape + click outside the popup. Skipped pointer-down on the
  // resize handle (which lives inside the popup) so resizing doesn't
  // accidentally close it.
  const popupRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The docked sidebar is persistent — only the floating overlay dismisses
    // itself on Escape / click-outside.
    if (variant !== 'overlay') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onPointerDown = (e: PointerEvent) => {
      const node = popupRef.current;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      if ((e.target as HTMLElement).closest?.('[data-order-actions-menu]')) return;
      // Toolbar toggle button has data-position-toggle so its own click (which
      // already toggles state in the parent) doesn't fire close on top of it.
      if ((e.target as HTMLElement).closest?.('[data-position-toggle]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onClose, variant]);

  const docked = variant === 'docked';
  const isFills = section === 'fills';
  const kalshiConnected = status === 'connected';
  return (
    <div
      ref={popupRef}
      className={
        docked
          ? // Content-sized, never stretched: the card ends where its rows
            // end (a tall monitor gets open rail below, not padded cards).
            // Full-bleed hard dock (Brendan, Jul 8): square corners, no
            // x-borders — the rail's border-l is the only vertical line;
            // border-y separates the card from the rail background.
            'bg-card border-y flex flex-col overflow-hidden border-gray-800'
          : 'bg-card fixed right-3 top-3 z-40 flex max-h-[calc(100vh-1.5rem)] flex-col rounded-lg border border-gray-800 shadow-2xl'
      }
      style={docked ? undefined : { width: `${width}px` }}
    >
      {/* Drag handle on the left edge — overlay only (docked width = the aside's) */}
      {!docked && (
        <div
          onPointerDown={handleResizePointerDown}
          className="absolute left-0 top-0 bottom-0 z-10 w-1 cursor-col-resize hover:bg-blue-500/40"
          title="Drag to resize"
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-800 px-3 py-2.5">
        <div className="flex items-center gap-2">
          {isFills ? (
            <>
              {onToggleExpand ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  className={`flex items-center gap-0.5 rounded p-0.5 transition-colors hover:bg-gray-800 ${
                    expanded ? 'text-primary hover:text-primary' : 'text-gray-400 hover:text-gray-200'
                  }`}
                  title={expanded ? 'Cap recent fills (scrolls inside)' : 'Expand recent fills to full height'}
                >
                  <Receipt size={14} />
                  {expanded ? <FoldVertical size={11} /> : <UnfoldVertical size={11} />}
                </button>
              ) : onToggleCollapse ? (
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  aria-expanded={!collapsed}
                  className="flex items-center gap-0.5 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"
                  title={collapsed ? 'Expand recent fills' : 'Collapse recent fills'}
                >
                  <Receipt size={14} />
                  <ChevronDown
                    size={11}
                    className={`transition-transform ${collapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              ) : (
                <Receipt size={14} className="text-gray-400" />
              )}
              <span className="text-sm font-medium text-gray-300" title="Kalshi and attributed Polymarket fills">
                Recent Fills
              </span>
            </>
          ) : (
            <>
              {onToggleExpand ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  className={`flex items-center gap-0.5 rounded p-0.5 transition-colors hover:bg-gray-800 ${
                    expanded ? 'text-primary hover:text-primary' : 'text-gray-400 hover:text-gray-200'
                  }`}
                  title={expanded ? 'Cap the tracker (scrolls inside)' : 'Expand the tracker to full height'}
                >
                  <Crosshair size={14} />
                  {expanded ? <FoldVertical size={11} /> : <UnfoldVertical size={11} />}
                </button>
              ) : onToggleCollapse ? (
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  aria-expanded={!collapsed}
                  className="flex items-center gap-0.5 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"
                  title={collapsed ? 'Expand the tracker' : 'Collapse the tracker'}
                >
                  <Crosshair size={14} />
                  <ChevronDown
                    size={11}
                    className={`transition-transform ${collapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              ) : (
                <Crosshair size={14} className="text-gray-400" />
              )}
              {onOpenDetail ? (
                <button
                  onClick={onOpenDetail}
                  className="text-sm font-medium text-gray-300 transition-colors hover:text-primary hover:underline"
                  title="Open Position Tracker — count/value are Kalshi; Polymarket positions live on the Positions page"
                >
                  Positions{' '}
                  <span className="text-gray-500">
                    {positionsWithPnl.length} / ${positionsValueDollars.toFixed(2)} (K)
                  </span>
                </button>
              ) : (
                <span
                  className="text-sm font-medium text-gray-300"
                  title="Count/value are Kalshi; Polymarket positions live on the Positions page"
                >
                  Positions{' '}
                  <span className="text-gray-500">
                    {positionsWithPnl.length} / ${positionsValueDollars.toFixed(2)} (K)
                  </span>
                </span>
              )}
              {/* Balances render in the OVERLAY header only — the docked
                  rail shows them in its pinned header band instead, so the
                  prong title row stays one line (Brendan, Jul 9). This
                  header branch only renders for the tracker/'all' prongs
                  (the fills prong takes the isFills branch above). */}
              {!docked && balance !== null && (
                <span className="whitespace-nowrap text-xs text-gray-500" title={kalshiReportedBalanceTitle(keyMode)}>
                  Bal K{' '}
                  <span className="text-gray-300">
                    ${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </span>
              )}
              {!docked && polyBalance.usd !== null && (
                <span
                  className="whitespace-nowrap text-xs text-gray-500"
                  title={`Polymarket ${polyBalance.source === 'org' ? 'org' : 'personal'} account (funder USDC)`}
                >
                  P{' '}
                  <span className="text-gray-300">
                    ${polyBalance.usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </span>
              )}
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  audioState === 'running' ? 'bg-green-500' : audioState === 'suspended' ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                title={`Audio: ${audioState}`}
              />
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-800 hover:text-gray-300"
            title={docked ? 'Hide this panel' : 'Close'}
            aria-label={docked ? 'Hide this panel' : 'Close'}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {!collapsed && (
      <div
        className={
          onToggleExpand
            ? expanded
              ? // Expanded accordion: full content height, no internal
                // scroll — the rail's stack scroller handles overflow.
                ''
              : // Resting accordion: capped at a real, usable height
                // (max-h-56 = 224px ≈ 6-8 fill rows) and scrolls inside.
                // Under the cap the card just ends at its content.
                'max-h-56 overflow-y-auto'
            : 'flex-1 overflow-y-auto'
        }
      >
        {/* Kalshi gate is a BANNER, not a body replacement — the poly money
            lines and poly order rows ride their own relay and must not blank
            during a Kalshi outage (Brendan, Jul-9; this gate's own copy says
            "Polymarket is unaffected"). Kalshi-sourced sections below gate
            individually instead. */}
        {!kalshiConnected && (
          <div className="px-3 py-4 text-center text-xs text-gray-500">
            Connect Kalshi in Settings
            <div className="mt-1 text-[10px]">
              (Kalshi maintenance runs Thu 3–5 AM ET; Polymarket is unaffected)
            </div>
          </div>
        )}
        <>
            {section !== 'fills' && (
            <>
            {/* P&L Summary — venue-split: usePositionPnl is Kalshi-only, and
                an unlabeled total reads as "the account". Poly rides its own
                hook (same formula as the tracker's headline tile). */}
            <div className="flex items-center gap-3 border-b border-gray-800/50 px-3 py-2.5">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-gray-500" title={MTM_PNL_TOOLTIP}>
                  Unrealized P&L · MTM
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[10px] text-gray-500" title={showPnl ? kalshiPnlCoverageTitle : 'Kalshi unrealized P&L'}>K</span>
                  <span
                    className={`text-sm font-semibold ${
                      !showPnl || totalPnl === null ? 'text-gray-500' : totalPnl >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}
                    title={showPnl ? kalshiPnlCoverageTitle : 'P&L hidden — toggle on the Positions page'}
                  >
                    {!positionsSync.synced ? '—' : showPnl ? kalshiPnlText : '•••'}
                  </span>
                  <span
                    className="text-[10px] text-gray-500"
                    title={polyPnlCoverageTitle}
                  >
                    P
                  </span>
                  <span
                    className={`text-sm font-semibold ${
                      !showPnl || polyPnlValue === null
                        ? 'text-gray-500'
                        : polyPnlValue >= 0
                          ? 'text-green-400'
                          : 'text-red-400'
                    }`}
                    title={
                      showPnl
                        ? polyPnlCoverageTitle
                        : 'P&L hidden — toggle on the Positions page'
                    }
                  >
                    {!showPnl
                      ? '•••'
                      : polyPnlValue != null
                        ? formatDollars(polyPnlValue)
                        : polyPnl == null
                          ? '—'
                          : `— (${polyPnl.pricedPositions}/${polyPnl.totalPositions})`}
                  </span>
                </div>
                <PositionsRefreshWarning positionsSync={positionsSync} showPnl={showPnl} title={kalshiPnlCoverageTitle} />
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-gray-500" title="Kalshi positions / open orders">Pos / Ord (K)</div>
                <div className="text-sm text-gray-300">
                  {kalshiConnected ? `${positionsWithPnl.length} / ${openOrders.length}` : '—'}
                </div>
              </div>
            </div>

            {/* Positions List (Kalshi-sourced — gated on SNAPSHOT evidence, so a
                transient reconnect keeps the holdings on screen and an
                unreadable snapshot never reads as "No open positions"). */}
            {positionsSync.synced && positionsWithPnl.length > 0 && (
              <div className="border-b border-gray-800/50">
                {positionsWithPnl.map((pos) => {
                  const { marketLabel, betTeam } = describeMarket(pos.ticker, pos.side);
                  return (
                  <div
                    key={`${pos.ticker}-${pos.side}`}
                    className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30"
                  >
                    <div className="flex items-center gap-1.5 overflow-hidden">
                      <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                        pos.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {betTeam ? `(${betTeam}) ` : ''}{pos.side === 'yes' ? 'Y' : 'N'}
                      </span>
                      <span className="truncate text-gray-300" title={pos.ticker}>
                        {marketLabel}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-gray-500">{fmtQty(pos.position)}</span>
                      <span className="text-gray-500">@{formatCents(pos.averagePrice)}</span>
                      {pos.unrealizedPnl !== null ? (
                        <span className={`w-14 text-right font-mono ${
                          !showPnl ? 'text-gray-500' : pos.unrealizedPnl >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {showPnl ? formatDollars(pos.unrealizedPnl) : '•••'}
                        </span>
                      ) : (
                        <span className="w-14 text-right text-gray-600">&mdash;</span>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {positionsSync.synced && positionsWithPnl.length === 0 && (
              <div className="border-b border-gray-800/50 px-3 py-4 text-center text-xs text-gray-500">
                No open positions
              </div>
            )}
            {kalshiConnected && !positionsSync.synced && (
              <div
                className="border-b border-gray-800/50 px-3 py-4 text-center text-xs text-amber-500"
                title={showPnl ? kalshiPnlCoverageTitle : undefined}
              >
                Positions unavailable
              </div>
            )}

            {/* Open Orders — both venues, venue-chipped like the positions
                page's unified tables. Kalshi rows keep edit + cancel; poly
                rows are display + cancel (price edits live on the Positions
                page). */}
            <div className="border-b border-gray-800/50">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-500">
                Open Orders
              </div>
              {kalshiConnected && openOrders.map((ord) => {
                const { marketLabel, betTeam } = describeMarket(ord.ticker, ord.side);
                // Kalshi often returns only yes_price even for NO orders; for
                // a NO bid the actual price is 100 - yes_price.
                const sidePrice =
                  ord.side === 'yes'
                    ? (ord.yesPrice ?? (ord.noPrice != null ? 100 - ord.noPrice : null))
                    : (ord.noPrice ?? (ord.yesPrice != null ? 100 - ord.yesPrice : null));
                return (
                <div
                  key={ord.orderId}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30"
                >
                  <div className="flex items-center gap-1.5 overflow-hidden">
                    <VenueChip venue="kalshi" />
                    <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                      ord.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {betTeam ? `(${betTeam}) ` : ''}{ord.side === 'yes' ? 'Y' : 'N'}
                    </span>
                    <span className="truncate text-gray-300" title={ord.ticker}>
                      {marketLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-gray-500">{ord.remainingCount}</span>
                    <span className="text-gray-500">
                      @{sidePrice != null ? formatCents(sidePrice) : '—'}
                    </span>
                    <span className={`rounded px-1 py-0.5 text-[9px] font-medium ${
                      ord.type === 'limit' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {ord.type === 'limit' ? 'Lim' : 'Mkt'}
                    </span>
                    <OrderActionButtons
                      // #241: this row previously passed no `canceling` at all,
                      // so its Cancel stayed live through the whole request —
                      // the worst of the three surfaces for double-clicking.
                      canceling={isKalshiCancelPending(kalshiCancelStates[ord.orderId])}
                      onCancel={() =>
                        requestCancel(
                          'kalshi',
                          ord.orderId,
                          betTeam ? `${marketLabel} (${betTeam})` : marketLabel,
                        )
                      }
                      onEdit={() => {
                        setEditOrder({
                          orderId: ord.orderId,
                          marketLabel: betTeam ? `${marketLabel} (${betTeam})` : marketLabel,
                          side: ord.side,
                          action: ord.action,
                          remainingCount: ord.remainingCount,
                          currentPriceCents: sidePrice ?? 50,
                          ticker: ord.ticker,
                        });
                      }}
                      editAvailable={ord.reconciliation?.actions.decrease !== 'unavailable'}
                      className="ml-0.5"
                    />
                  </div>
                </div>
                );
              })}
              {polyOrdersState.orders.map((o, i) => {
                const id = String(o.id ?? o.orderID ?? o.orderId ?? '');
                const conditionId = String(o.market ?? '');
                // Market-level label (orientation-free): the CLOB row's
                // BUY/SELL says bid vs ask; the outcome-token side chip the
                // tracker shows needs the token desc and isn't worth a
                // sweep here.
                const label =
                  describePolyMarketCached(conditionId)?.matchup ??
                  (conditionId.length > 14
                    ? `${conditionId.slice(0, 6)}…${conditionId.slice(-4)}`
                    : conditionId || id);
                const isSell = String(o.side ?? '').toUpperCase() === 'SELL';
                const priceFrac =
                  typeof o.price === 'number' ? o.price : Number(o.price ?? NaN);
                // Centi-cent grain — a resting 58.25¢ CLOB order must not
                // display as 58¢ (sub-cent contract).
                const priceCents = Number.isFinite(priceFrac)
                  ? Math.round(priceFrac * 10000) / 100
                  : null;
                // Shared derivation with the hook's replace flow; CLOB
                // sizes are fractional — round for display.
                const remainingRaw = polyOpenOrderRemaining(o);
                const remaining = remainingRaw != null ? Math.round(remainingRaw) : null;
                // #748 — the one shared answer, so this row agrees with every
                // other mount showing the same order.
                const cancelRow = describePolyCancelRow(polyCancelStates[id]);
                return (
                  <div
                    key={id ? `poly-${id}` : `poly-row-${i}`}
                    className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30"
                  >
                    <div className="flex items-center gap-1.5 overflow-hidden">
                      <VenueChip venue="polymarket" />
                      <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                        isSell ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                      }`}>
                        {isSell ? 'ASK' : 'BID'}
                      </span>
                      <span className="truncate text-gray-300" title={conditionId || id}>
                        {label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-gray-500">{remaining ?? '—'}</span>
                      <span className="text-gray-500">
                        @{priceCents != null ? formatCents(priceCents) : '—'}
                      </span>
                      {/* An id-less row can't be cancelled — a bare id would
                          DELETE /api/polymarket/orders/ (safe 404, but noise). */}
                      {id !== '' && (
                        <button
                          type="button"
                          onClick={() =>
                            // Full order identity in the confirm — two resting
                            // orders on the same market must be tellable apart.
                            requestCancel(
                              'polymarket',
                              id,
                              `${label} ${isSell ? 'ASK' : 'BID'} ${remaining ?? '—'} @ ${
                                priceCents != null ? `${priceCents}¢` : '—'
                              }`,
                            )
                          }
                          disabled={cancelRow.pending}
                          title={cancelRow.message ?? 'Cancel order'}
                          aria-label={cancelRow.message ?? 'Cancel Polymarket order'}
                          className={
                            cancelRow.label
                              ? `inline-flex h-6 shrink-0 items-center rounded px-1.5 text-[10px] font-medium ${
                                  // Amber is this sidebar's established
                                  // could-not-confirm tone (the #241 notice
                                  // below uses it); a pending row stays neutral
                                  // because nothing has gone wrong yet.
                                  cancelRow.unconfirmed
                                    ? 'text-amber-300 hover:bg-amber-500/10'
                                    : 'text-gray-400'
                                } disabled:cursor-default`
                              : 'inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-400'
                          }
                        >
                          {cancelRow.label ?? <X size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {(!kalshiConnected || openOrders.length === 0) && polyOrdersState.orders.length === 0 && (
                <div className="px-3 py-2 text-center text-xs text-gray-600">
                  No open orders
                </div>
              )}
            </div>
            </>
            )}

            {/* Recent Fills. As its own rail prong (section 'fills') the prong
                header carries the title, so the inline sub-header + /fills
                pop-out button is only for the 'all' overlay. */}
            {/* NOT gated on kalshiConnected (#457): the tape is two-venue now,
                so a Kalshi outage must not blank attributed Polymarket fills —
                same rule the poly money lines and order rows already follow.
                ActivityTicker owns its own empty/unavailable states. */}
            {section !== 'tracker' && (
              isFills ? (
                // px-1 so the fill rows' content lines up with the tracker
                // prong's px-3 rows (ActivityTicker rows are px-2; the +4px
                // wrapper matches — same as the 'all' overlay below).
                <div className="px-1 py-1">
                  <ActivityTicker />
                </div>
              ) : (
                <div className="px-1 py-1">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-500">
                      Recent Fills
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        // Named window so re-clicking the button focuses the
                        // existing popup instead of spawning a duplicate.
                        window.open(
                          '/fills',
                          'g1-fills-popup',
                          'width=420,height=640,toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes',
                        );
                      }}
                      className="text-gray-500 hover:bg-gray-700/50 hover:text-gray-300 rounded p-0.5 transition-colors"
                      title="Open in a separate window"
                      aria-label="Pop out recent fills"
                    >
                      <ExternalLink size={10} />
                    </button>
                  </div>
                  <ActivityTicker />
                </div>
              )
            )}
        </>
      </div>
      )}

      {cancelError && (
        <div className="mx-3 mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          <div className="flex items-start justify-between gap-2">
            <span>{cancelError}</span>
            <button
              type="button"
              onClick={() => setCancelError(null)}
              className="text-red-300/70 hover:text-red-200"
              aria-label="Dismiss cancel error"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Separate from cancelError so an immediate failure and a stalled
          synchronization can't mask one another (#241). */}
      {timedOutCancels.length > 0 && (
        <div className="mx-3 mb-3 space-y-1 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
          {timedOutCancels.map(([orderId, state]) => (
            <div key={orderId}>
              <span className="font-mono text-[10px]">{orderId}</span>: {state?.message}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        onConfirm={() => {
          // Clear the captured target before firing so the modal can't
          // re-submit; the store's duplicate guard is the final safety net.
          const target = pendingCancel;
          setPendingCancel(null);
          if (target) void executeCancel(target.venue, target.orderId, target.marketSlug);
        }}
        title="Cancel order?"
        message={pendingCancel ? `Cancel ${pendingCancel.label}?` : 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />

      {editOrder && (
        <EditOrderPriceModal
          isOpen={editOrder !== null}
          onClose={() => setEditOrder(null)}
          onReplace={async (newPriceCents, newQuantity, options) => {
            // accountStore.replaceOrder smart-routes by default: same-price
            // qty-shrink goes through /decrease (queue-preserving); anything
            // else is cancel + new-place. The modal's force-cancel toggle
            // forwards as options.forceCancelReplace, overriding the smart
            // route when set. Sidebar only renders Kalshi orders today.
            await replaceOrder(
              editOrder.orderId,
              newPriceCents,
              newQuantity,
              options?.forceCancelReplace,
            );
            setEditOrder(null);
          }}
          marketLabel={editOrder.marketLabel}
          side={editOrder.side}
          action={editOrder.action}
          remainingCount={editOrder.remainingCount}
          currentPriceCents={editOrder.currentPriceCents}
          ticker={editOrder.ticker}
          venue="kalshi"
        />
      )}
    </div>
  );
}

// ── PositionsWidget — compact toolbar-height readout + popup toggle ──

/**
 * Lightweight P&L / counts readout that sits in the toolbar row, with the
 * crosshair as the expand control for the floating popup. Sized via
 * `self-stretch` on a flex parent so its vertical extent matches the
 * adjacent toolbar.
 */
export function PositionsWidget({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const status = useAccountStore((s) => s.status);
  const openOrders = useAccountStore((s) => s.openOrders);
  const balance = useAccountStore((s) => s.balance);
  const keyMode = useAccountStore((s) => s.keyMode);
  const { positionsWithPnl, totalPnl, coverage, positionsSync, unpricedSummary } = usePositionPnl();
  const kalshiPnlCoverageTitle = kalshiPnlTitle(totalPnl, coverage, positionsSync, unpricedSummary);
  const kalshiPnlText = !positionsSync.synced || positionsSync.consecutiveFailures > 0
    ? '—'
    : totalPnl === null
      ? withheldHeadlineText(coverage, formatDollars)
      : formatDollars(totalPnl);
  // Venue-split money lines: usePositionPnl/accountStore are KALSHI-only,
  // and rendering them unlabeled reads as "the account". Poly twins ride
  // their own hooks (org-key relay balance with personal fallback; poly
  // unrealized from the same formula as the tracker tile). NOT gated on
  // the Kalshi connection — a Kalshi outage must not blank poly numbers.
  const polyBalance = usePolyBalance();
  const polyPnl = usePolyUnrealizedPnl();
  const polyPnlValue = polyPnl?.valueCents ?? null;
  const polyPnlCoverageTitle =
    polyPnl == null
      ? 'Polymarket unrealized P&L is loading'
      : polyPnl.complete
        ? `${polyPnl.pricedPositions}/${polyPnl.totalPositions} Polymarket positions fully priced`
        : `Polymarket unrealized P&L withheld: ${polyPnl.pricedPositions}/${polyPnl.totalPositions} positions have a live mark, open-fee allocation, and close-fee schedule`;
  const contractCount = positionsWithPnl.reduce((sum, p) => sum + Math.abs(p.position), 0);
  // Mark-to-market dollar value across all open positions; falls back to
  // averagePrice when hydra hasn't streamed a current mark yet so the figure
  // stays populated. Mirrors the Position Tracker stat-card calculation.
  const contractsValueDollars = positionsWithPnl.reduce((sum, p) => {
    const priceCents = p.currentPrice ?? p.averagePrice;
    return sum + (priceCents * Math.abs(p.position)) / 100;
  }, 0);
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const pnlClass = !showPnl || totalPnl === null
    ? 'text-muted-foreground'
    : totalPnl >= 0
      ? 'text-positive'
      : 'text-negative';

  return (
    <button
      data-position-toggle
      onClick={onToggle}
      title={expanded ? 'Hide positions' : 'Show positions'}
      aria-label={expanded ? 'Hide positions popup' : 'Show positions popup'}
      className={cn(
        'flex h-full w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
        expanded ? 'bg-primary/10' : 'hover:bg-muted/50',
      )}
    >
      {!positionsSync.synced && status !== 'connected' ? (
        <span className="text-muted-foreground text-xs">Not connected</span>
      ) : (
        <div className="flex flex-col justify-center gap-0.5">
          <div className="flex items-baseline gap-1.5">
            <span
              className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider"
              title={MTM_PNL_TOOLTIP}
            >
              Unrealized P&amp;L · MTM
            </span>
            <span
              className="text-muted-foreground font-mono text-[10px]"
              title={showPnl ? kalshiPnlCoverageTitle : 'Kalshi unrealized P&L'}
            >
              K
            </span>
            <span
              className={cn('font-mono text-sm font-semibold', pnlClass)}
              title={showPnl ? kalshiPnlCoverageTitle : 'P&L hidden — toggle on the Positions page'}
            >
              {showPnl ? kalshiPnlText : '•••'}
            </span>
            <span
              className="text-muted-foreground font-mono text-[10px]"
              title={polyPnlCoverageTitle}
            >
              P
            </span>
            <span
              className={cn(
                'font-mono text-sm font-semibold',
                !showPnl || polyPnlValue === null
                  ? 'text-muted-foreground'
                  : polyPnlValue >= 0
                    ? 'text-positive'
                    : 'text-negative',
              )}
              title={
                showPnl
                  ? polyPnlCoverageTitle
                  : 'P&L hidden — toggle on the Positions page'
              }
            >
              {!showPnl
                ? '•••'
                : polyPnlValue != null
                  ? formatDollars(polyPnlValue)
                  : polyPnl == null
                    ? '—'
                    : `— (${polyPnl.pricedPositions}/${polyPnl.totalPositions})`}
            </span>
          </div>
          <PositionsRefreshWarning positionsSync={positionsSync} showPnl={showPnl} title={kalshiPnlCoverageTitle} />
          <div className="flex items-baseline gap-1.5">
            <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
              Balance
            </span>
            <span
              className="text-muted-foreground font-mono text-[10px]"
              title={kalshiReportedBalanceTitle(keyMode)}
            >
              K
            </span>
            {balance !== null ? (
              <span className="text-foreground font-mono text-sm font-semibold">
                ${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            ) : (
              <span className="text-muted-foreground font-mono text-[11px]">—</span>
            )}
            <span
              className="text-muted-foreground font-mono text-[10px]"
              title={
                polyBalance.source === 'org'
                  ? 'Polymarket org account (funder USDC)'
                  : polyBalance.source === 'personal'
                    ? 'Polymarket personal account (funder USDC)'
                    : 'Polymarket balance — no org poly keys and no personal connection'
              }
            >
              P
            </span>
            {polyBalance.usd !== null ? (
              <span className="text-foreground font-mono text-sm font-semibold">
                ${polyBalance.usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            ) : (
              <span className="text-muted-foreground font-mono text-[11px]">—</span>
            )}
          </div>
          <div
            className="text-muted-foreground font-mono text-[11px]"
            title="Kalshi contracts / mark value — Polymarket positions live on the Positions page"
          >
            <span className="text-foreground/80">{fmtQty(contractCount)}</span> contract
            {Math.round(contractCount) === 1 ? '' : 's'}{' '}
            <span className="text-foreground/60">
              / ${contractsValueDollars.toFixed(2)} (K)
            </span>
          </div>
          <div
            className="text-muted-foreground font-mono text-[11px]"
            title="Kalshi open orders — Polymarket orders show in the positions panel and on the Positions page"
          >
            <span className="text-foreground/80">{openOrders.length}</span> order
            {openOrders.length === 1 ? '' : 's'} (K)
          </div>
        </div>
      )}
      <Crosshair
        size={16}
        className={expanded ? 'text-primary' : 'text-muted-foreground'}
      />
    </button>
  );
}
